import { memo, useMemo } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { CustomButtonProps } from 'types/props/CustomButtonProps';
import { colors } from 'styles/colors';
import { useMediaQuery } from '@mui/material';

function CustomButton({
  text,
  variant = 'filled',
  disabled = false,
  isLoading = false,
  intent = 'primary',
  icon,
  fontSize = 14,
  textColor,
  fontWeight = 500,
  height = 45,
  type = 'button',
  radius = 3,
  minWidth = 0,
  ...props
}: CustomButtonProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const muiVariant = variant === 'outlined' ? 'outlined' : 'contained';

  const resolvedTextColor =
    variant === 'outlined'
      ? colors[intent]
      : textColor
        ? colors[textColor]
        : theme.palette.common.white;

  const sxStyles = useMemo(() => {
    return {
      height: props.size ? null : height,
      fontSize,
      fontWeight,
      textTransform: 'none',
      color: resolvedTextColor,
      borderRadius: '10px',
      paddingRight: '20px',
      paddingLeft: '20px',
      minWidth: minWidth,
      whiteSpace: 'nowrap',

      /* Generic hover fallback */
      '&:hover': {
        color: resolvedTextColor,
      },

      /* IMPORTANT: variant-specific hover overrides */
      '&.MuiButton-outlined:hover': {
        color: resolvedTextColor,
      },

      '&.MuiButton-contained:hover': {
        color: resolvedTextColor,
      },

      /* Keep icons from changing color */
      '&:hover .MuiButton-startIcon, &:hover .MuiButton-endIcon': {
        color: resolvedTextColor,
      },

      ...(variant === 'outlined' && {
        borderColor: colors[intent],
      }),

      ...(intent === 'transparent' && {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: 'transparent',
        },
      }),
    };
  }, [height, fontSize, fontWeight, resolvedTextColor, variant, intent]);

  return (
    <Button
      type={type}
      variant={muiVariant}
      disabled={disabled || isLoading}
      startIcon={!isLoading ? icon : undefined}
      sx={sxStyles}
      {...props}
    >
      {isLoading ? (
        <CircularProgress
          size={24}
          sx={{
            color: variant === 'outlined' ? colors[intent] : (textColor ? colors[textColor] : theme.palette.common.white),
          }}
        />
      ) : (
        <Box component="span">{text}</Box>
      )}
    </Button>
  );
}

export default memo(CustomButton);
